import { useMemo } from 'react';
import { isEqual } from 'lodash';

import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';
import type { LatLonCoordinates } from '@e-socna/gis';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useAltiCodingQuery } from '../../../components/Map/hooks/geopl/useAltiCodingQuery.hook';
import { useGeoCodingReverseQuery } from '../../../components/Map/hooks/geopl/useGeoCodingReverseQuery.hook';
import { useAdministrativeDivisionQuery } from '../../../components/Map/hooks/geo/useAdministrativeDivisionQuery.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useMemoCompare } from '../../../hooks/useMemoCompare.hook';

import { AddressFormTab } from '../enums/tab.enum';
import type { AddressForm } from '../types/address-form.type';

interface Params {
  readonly latLon: LatLonCoordinates | undefined;
  readonly enabled?: boolean;
}

type Result =
  | Readonly<{
      isError: false;
      isLoading: true;
      data: undefined;
    }>
  | Readonly<{
      isError: false;
      isLoading: false;
      data: AddressForm;
    }>
  | Readonly<{
      isError: true;
      isLoading: false;
      data: undefined;
    }>;

export const useFromLatLonToAddressForm = ({
  latLon,
  enabled = !!latLon,
}: Readonly<Params>): Result => {
  const { generateId } = useIdFactory();

  const { projectionsCodeMap } = useReferencesContext();

  const {
    data: addressQueryData,
    isError: addressQueryIsError,
    isSuccess: addressQueryIsSuccess,
  } = useGeoCodingReverseQuery({
    position: latLon,
    index: 'address',
    enabled: !!(enabled && latLon),
  });

  const {
    data: altiQueryData,
    isError: altiQueryIsError,
    isSuccess: altiQueryIsSuccess,
  } = useAltiCodingQuery({
    position: latLon,
    enabled: !!(enabled && latLon),
  });

  const {
    data: parcelQueryData,
    isError: parcelQueryIsError,
    isSuccess: parcelQueryIsSuccess,
  } = useGeoCodingReverseQuery({
    position: latLon,
    index: 'parcel',
    enabled: !!(enabled && latLon),
  });

  const addressQueryDataFeatureProperties =
    addressQueryData?.features[0]?.properties;

  const cityCode = addressQueryDataFeatureProperties?.citycode;

  const {
    data: administrativeQueryData,
    isError: administrativeQueryIsError,
    isSuccess: administrativeQueryIsSuccess,
  } = useAdministrativeDivisionQuery({
    inseeCode: cityCode ?? EMPTY_STRING,
    enabled: !!(enabled && cityCode && latLon),
  });

  const isError =
    addressQueryIsError ||
    altiQueryIsError ||
    parcelQueryIsError ||
    administrativeQueryIsError;

  const altitude = altiQueryData?.elevations[0]?.z;
  const parcelQueryFeature = parcelQueryData?.features[0]?.properties;

  const result: Result = useMemo(() => {
    const enabledSteps: AddressFormTab[] = [
      ...(parcelQueryFeature ? [AddressFormTab.CadastralInfo] : EMPTY_ARRAY),
      ...(altitude ? [AddressFormTab.LocationInfo] : EMPTY_ARRAY),
      ...(addressQueryDataFeatureProperties && administrativeQueryData
        ? [AddressFormTab.PostalInfo]
        : EMPTY_ARRAY),
    ];

    let streetNumber = EMPTY_STRING;
    let route = EMPTY_STRING;
    switch (addressQueryDataFeatureProperties?.type) {
      case 'housenumber':
        streetNumber =
          addressQueryDataFeatureProperties.housenumber ?? EMPTY_STRING;
        route = addressQueryDataFeatureProperties.street ?? EMPTY_STRING;
        break;
      case 'street':
        route = addressQueryDataFeatureProperties.name ?? EMPTY_STRING;
        break;
      case 'locality':
      case 'municipality':
      case undefined:
      default:
        if (addressQueryDataFeatureProperties) {
          console.warn(
            `Unknown feature type: ${addressQueryDataFeatureProperties.type}`,
            addressQueryDataFeatureProperties,
          );
        }
    }

    if (isError) {
      return { isError: true, isLoading: false, data: undefined };
    }

    return !addressQueryIsSuccess ||
      !altiQueryIsSuccess ||
      !parcelQueryIsSuccess ||
      (cityCode && !administrativeQueryIsSuccess) ||
      !latLon
      ? {
          isError: false,
          isLoading: true,
          data: undefined,
        }
      : {
          isError: false,
          isLoading: false,
          data: {
            __typename: 'Address' as const,
            _id: generateId(),
            typeIds: EMPTY_ARRAY,
            enabledSteps,
            postalInfo: {
              __typename: 'PostalInfo' as const,
              _id: generateId(),
              postOfficeBox: EMPTY_STRING,
              postalCode:
                addressQueryDataFeatureProperties?.postcode ?? EMPTY_STRING,
              region: administrativeQueryData?.region.nom ?? EMPTY_STRING,
              route,
              streetNumber,
              additional: EMPTY_STRING,
              building: EMPTY_STRING,
              businessPostCode: null,
              locality: addressQueryDataFeatureProperties?.city ?? EMPTY_STRING,
              inseeCode:
                addressQueryDataFeatureProperties?.citycode ?? EMPTY_STRING,
              department:
                administrativeQueryData?.departement.nom ?? EMPTY_STRING,
              countryAlpha2: addressQueryDataFeatureProperties
                ? 'FR'
                : EMPTY_STRING,
            },
            locationInfo:
              enabledSteps.includes(AddressFormTab.LocationInfo) && altitude
                ? {
                    __typename: 'LocationInfo' as const,
                    _id: generateId(),
                    coordinates: [latLon.lon, latLon.lat],
                    projectionId: projectionsCodeMap.Epsg4326._id,
                    altitude,
                  }
                : null,
            cadastralInfo:
              enabledSteps.includes(AddressFormTab.CadastralInfo) &&
              parcelQueryFeature
                ? {
                    __typename: 'CadastralInfo' as const,
                    _id: generateId(),
                    departmentCode:
                      parcelQueryFeature.departmentcode ?? EMPTY_STRING,
                    districtCode:
                      parcelQueryFeature.districtcode ?? EMPTY_STRING,
                    municipalityCode:
                      parcelQueryFeature.municipalitycode ?? EMPTY_STRING,
                    number: parcelQueryFeature.number ?? EMPTY_STRING,
                    oldMunicipalityCode:
                      parcelQueryFeature.oldmunicipalitycode ?? EMPTY_STRING,
                    section: parcelQueryFeature.section ?? EMPTY_STRING,
                    sheet: parcelQueryFeature.sheet ?? EMPTY_STRING,
                  }
                : null,
          },
        };
  }, [
    addressQueryDataFeatureProperties,
    addressQueryIsSuccess,
    administrativeQueryData,
    administrativeQueryIsSuccess,
    altitude,
    altiQueryIsSuccess,
    cityCode,
    generateId,
    isError,
    latLon,
    parcelQueryIsSuccess,
    projectionsCodeMap.Epsg4326._id,
    parcelQueryFeature,
  ]);

  return useMemoCompare(result, isEqual);
};
